import * as actions from '@actions';

function isNumeric(str: string) {
  if (typeof str !== 'string') return false; // we only process strings!
  return (
    !Number.isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !Number.isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

interface PDFImage {
  name: any;
  content: any;
  type: any;
  width: any;
  height: any;
}

export async function scaleImages(responses, definition, surveyObject, case_id?, dispatch?) {
  // Set max width and height to twice of original sizes. So that can be down scaled in generatePdf()
  const scaleDimensions = (imageWidth: number, imageHeight: number) => {
    if (imageHeight > imageWidth) {
      const maxHeight = 1200;

      if (imageHeight > maxHeight) {
        return { width: imageWidth / (imageHeight / maxHeight), height: maxHeight };
      }
    } else {
      const maxWidth = 900;

      if (imageWidth > maxWidth) {
        return { width: maxWidth, height: imageHeight / (imageWidth / maxWidth) };
      }
    }

    return { width: imageWidth, height: imageHeight };
  };

  const resizeImage = async (image: PDFImage, key: number) => {
    const { user_guid, patient_user_guid, cat_code, survey_id } = surveyObject;
    const { content } = image;
    const { type } = image;
    const filename = `${case_id || survey_id}doc${key + 1}.${image.name.split('.').pop()}`;
    const base64 = content.split(',')[1];
    const truncatedBase64 = base64.slice(0, 4 * 1024);
    const reqBody = {
      user_guid,
      filename,
      case_id
    };
    if (!cat_code || cat_code !== 'GH') {
      const result = await dispatch(actions.uploadCaseDocsDatabase(reqBody));
      if (result.type === 'UPLOAD_DOCS_DATABASE_SUCCESS') {
        const { payload } = await dispatch(
          actions.uploadDocs({
            user_guid: user_guid || patient_user_guid,
            filename,
            base64: truncatedBase64,
            tempurl: true
          })
        );
        if (payload.url) {
          const blob = await (await fetch(`data:${payload.mime};base64,${base64}`)).blob();
          const response = await fetch(payload.url, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': payload.mime,
              'Content-Disposition': 'inline',
              redirect: 'follow'
            }
          });
          console.log({ response });
        }
      } else {
        console.log('Fail to upload file');
      }
    }

    if (!type.includes('image/') && cat_code !== 'GH') {
      // acknowledge file uploaded
      const { type } = await dispatch(
        actions.uploadDocs({
          user_guid: user_guid || patient_user_guid,
          filename,
          uploaded: true
        })
      );

      if (type !== 'UPLOAD_DOCS_SUCCESS') {
        throw Error('File upload failed');
      }
      return;
    }

    if (!type.includes('image/') && cat_code === 'GH') {
      // acknowledge file uploaded
      const { type } = await dispatch(
        actions.uploadDocs({
          user_guid: user_guid || patient_user_guid,
          survey_id,
          filename,
          uploaded: true
        })
      );

      if (type !== 'UPLOAD_DOCS_SUCCESS') {
        throw Error('File upload failed');
      }
      return;
    }
    const canvas = document.createElement('canvas');
    const img = new Image();
    const context = canvas.getContext('2d');

    if (!context) {
      throw Error('Device does not support canvas required for image manipulation');
    }

    const p = new Promise((resolve) => {
      img.src = content;
      img.onload = resolve;
    });

    await p;

    const { width, height } = scaleDimensions(img.width, img.height);

    canvas.width = width;
    canvas.height = height;
    context.drawImage(img, 0, 0, width, height);

    image.content = canvas.toDataURL('image/jpeg', 0.5);
    image.width = width;
    image.height = height;
  };

  const images: PDFImage[] = [];
  // this stringify/parse effectively "visits" each key/value pair in the survey DEFINITION. Then, for each file type
  // question it extracts the file response from "responses" and stores that away in images.
  const def = JSON.parse(JSON.stringify(definition), (key, value) => {
    if (isNumeric(key) && value?.type === 'file' && responses && value?.name && responses[value.name]) {
      images.push(...responses[value.name]);
      // If we need to replace non-image files with placeholder to avoid sending large data in survey response
      // Here would be where to do it I think.
      // However, have verified that non-image files are NOT currently being sent to BE in survey response
    }

    /*
     * Imagepicker type questions with base64-encoded SVGs break the PDF
     * generation. This workaround replaces base64-encoded SVGs in the survey
     * definition with a placeholder image.
     */
    if (key === 'imageLink' && typeof value === 'string' && value.includes('data:image/svg+xml')) {
      // A single orange pixel
      value = 'data:image/gif;base64,R0lGODlhAQABAPAAALtXKAAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==';
    }

    return value;
  });

  // now that all the images are stored in the images array, destructively resize them. This should update the
  // images in the responses
  await Promise.all(images.map((i, key) => resizeImage(i, key)));

  return { responses, definition: def };
}

export default scaleImages;
